import React from 'react'
import { Box, Link, Skeleton, Tooltip, Typography } from '@mui/material'
import { C, cardSx, tabular } from '../RestaurantDashboard/tokens'
import { cardSubSx, cardTitleSx } from '../RestaurantDashboard/SalesOverTime'
import { fullDateTimeIn } from '../../utils/restaurantTime'
import { $, CHANNEL_LABEL, FULFILL_LABEL, NOT_TRACKED, count } from './saUtil'

const ORDER_LINK = id => `#/super_admin/orders?open=${encodeURIComponent(`o:${id}`)}`

function Stat({ label, value, hint, href, muted }) {
  const v = (
    <Typography sx={{ ...tabular, fontSize: 18, fontWeight: 600, color: muted ? C.textMuted : C.text, lineHeight: 1.25 }}>{value}</Typography>
  )
  return (
    <Box sx={{ py: 1, borderBottom: `1px solid ${C.border}`, '&:last-of-type': { borderBottom: 0 }, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ fontSize: 13, color: C.textMuted }}>{label}</Typography>
      <Box sx={{ textAlign: 'right' }}>
        {href ? <Link href={href} underline="hover" sx={{ color: 'inherit' }}>{v}</Link> : hint ? <Tooltip title={hint} arrow><span tabIndex={0}>{v}</span></Tooltip> : v}
      </Box>
    </Box>
  )
}

function Panel({ title, sub, children, loading }) {
  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
      <Typography component="h2" sx={cardTitleSx}>{title}</Typography>
      <Typography sx={cardSubSx}>{sub}</Typography>
      <Box sx={{ mt: 1.5 }}>{loading ? [0, 1, 2, 3, 4].map(i => <Skeleton key={i} height={34} />) : children}</Box>
    </Box>
  )
}

const pctText = r => (r == null ? '—' : `${(r * 100).toFixed(1)}%`)

export function OperationsCards({ data, loading }) {
  const d = data?.drivers
  const h = data?.health
  const p = data?.platform
  const first = loading && !data
  const payoutTracked = d && d.deliveryOrdersWithPayout > 0
  return (
    <Box component="section" aria-label="Operations" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: 'repeat(3, 1fr)' } }}>
      <Panel title="Drivers" sub="Deliveries in this range" loading={first}>
        {d ? (
          <>
            <Stat label="Available right now" value={`${count(d.availableNow)} of ${count(d.totalRiders)}`} />
            <Stat label="Deliveries completed" value={count(d.deliveriesCompleted)} />
            <Stat label="Avg tip per delivery" value={d.avgTipPerDelivery == null ? '—' : $(d.avgTipPerDelivery)} />
            <Stat label="Total driver payouts" value={payoutTracked ? $(d.driverPayouts) : '—'} muted={!payoutTracked}
              hint={payoutTracked ? `Covers ${d.deliveryOrdersWithPayout} of ${d.deliveryOrders} deliveries` : NOT_TRACKED} />
            <Stat label="Pending payout requests" value={count(d.pendingPayoutRequests)} href="#/super_admin/withdraw/" />
          </>
        ) : null}
      </Panel>

      <Panel title="Order health" sub="All dashboard orders in this range" loading={first}>
        {h ? (
          <>
            <Stat label="Cancellation rate" value={pctText(h.cancellationRate)} />
            <Stat label="Refund rate" value={pctText(h.refundRate)} />
            <Stat label="Avg delivery time" value={h.avgDeliveryMinutes == null ? '—' : `${h.avgDeliveryMinutes} min`} hint="Placed to delivered, delivery orders only" />
            <Box sx={{ pt: 1 }}>
              <Typography sx={{ fontSize: 13, color: C.textMuted }}>
                Stuck in pending &gt; 15 min <Box component="span" sx={{ fontWeight: 700, color: h.stuckPending.length ? C.downText : C.text }}>· {h.stuckPending.length}</Box>
              </Typography>
              {h.stuckPending.length ? (
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.75, maxHeight: 132, overflow: 'auto' }}>
                  {h.stuckPending.map(s => (
                    <Box component="li" key={s.id}>
                      <Link href={ORDER_LINK(s.id)} underline="hover" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.5, fontSize: 13, color: C.text }}>
                        <span>#{s.orderId} · {s.restaurantName}</span>
                        <Box component="span" sx={{ ...tabular, color: C.downText, fontWeight: 600, whiteSpace: 'nowrap' }}>{s.minutes} min</Box>
                      </Link>
                    </Box>
                  ))}
                </Box>
              ) : <Typography sx={{ fontSize: 12.5, color: C.upText, mt: 0.5 }}>Nothing waiting right now.</Typography>}
            </Box>
          </>
        ) : null}
      </Panel>

      <Panel title="Platform" sub="Restaurants, customers and vendors" loading={first}>
        {p ? (
          <>
            <Stat label="Restaurants" value={`${count(p.restaurantsActive)} active · ${count(p.restaurantsInactive)} inactive`} />
            <Stat label="New restaurants this period" value={count(p.newRestaurants)} />
            <Stat label="Customers who ordered" value={count(p.customers)} />
            <Stat label="New vs returning" value={`${count(p.newCustomers)} new · ${count(Math.max(0, p.customers - p.newCustomers))} returning`} />
            <Stat label="Vendors" value={count(p.vendors)} />
          </>
        ) : null}
      </Panel>
    </Box>
  )
}

function statusBadge(s) {
  const k = String(s || '').toUpperCase()
  if (k === 'REFUNDED') return { label: 'Refunded', color: C.amberText, bg: C.amberSoft }
  if (k === 'CANCELLED') return { label: 'Cancelled', color: '#4B5563', bg: '#F3F4F6' }
  if (['DELIVERED', 'PICKED'].includes(k)) return { label: 'Completed', color: C.upText, bg: C.upSoft }
  return { label: 'In progress', color: C.skyText, bg: C.skySoft }
}

const RECENT_COLS = [
  { key: 'number', label: 'Order #', align: 'left' },
  { key: 'restaurantName', label: 'Restaurant', align: 'left' },
  { key: 'placedAt', label: 'Time', align: 'left' },
  { key: 'channel', label: 'Channel', align: 'left' },
  { key: 'fulfillment', label: 'Fulfillment', align: 'left' },
  { key: 'itemSubtotal', label: 'Subtotal' },
  { key: 'discounts', label: 'Discount' },
  { key: 'tips', label: 'Tip' },
  { key: 'commission', label: 'Commission' },
  { key: 'grandTotal', label: 'Grand total' },
  { key: 'status', label: 'Status' }
]

export function RecentOrdersTable({ data, loading }) {
  const orders = data?.recentOrders || []
  const cell = { px: 1.5, py: 1.1, whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }
  const render = (o, k) => {
    switch (k) {
      case 'number': return <Link href={ORDER_LINK(o.id)} underline="hover" sx={{ fontWeight: 600, color: C.text }}>#{o.number}</Link>
      case 'placedAt': return fullDateTimeIn(o.placedAt, o.restaurantTimezone)
      case 'channel': return CHANNEL_LABEL[o.channel] || o.channel
      case 'fulfillment': return FULFILL_LABEL[o.fulfillment] || o.fulfillment
      case 'commission': return o.commission == null ? <Tooltip title={NOT_TRACKED}><Box component="span" sx={{ color: C.textMuted }}>—</Box></Tooltip> : $(o.commission)
      case 'status': { const b = statusBadge(o.status); return <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: b.color, bgcolor: b.bg, px: 1, py: 0.25, borderRadius: '999px' }}>{b.label}</Box> }
      case 'restaurantName': return o.restaurantName || '—'
      default: return $(o[k])
    }
  }
  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography component="h2" sx={cardTitleSx}>Recent orders</Typography>
          <Typography sx={cardSubSx}>The latest dashboard orders in this range, all restaurants</Typography>
        </Box>
        <Link href="#/super_admin/orders" underline="hover" sx={{ fontSize: 13, fontWeight: 600, color: C.brandText, whiteSpace: 'nowrap' }}>View all</Link>
      </Box>
      <Box sx={{ mt: 2, overflowX: 'auto', ...tabular }} tabIndex={0} aria-label="Recent orders, scrollable">
        {loading && !data ? [0, 1, 2, 3].map(i => <Skeleton key={i} height={36} />) : orders.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: C.textMuted, py: 1 }}>No orders in this range.</Typography>
        ) : (
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <Box component="thead">
              <Box component="tr">
                {RECENT_COLS.map(c => <Box component="th" key={c.key} scope="col" sx={{ ...cell, textAlign: c.align || 'right', fontSize: 12, fontWeight: 600, color: C.textMuted, bgcolor: '#F9FAFB' }}>{c.label}</Box>)}
              </Box>
            </Box>
            <Box component="tbody">
              {orders.map(o => (
                <Box component="tr" key={o.id} sx={{ '&:hover td': { bgcolor: '#F9FAFB' } }}>
                  {RECENT_COLS.map(c => <Box component="td" key={c.key} sx={{ ...cell, textAlign: c.align || 'right', color: C.text }}>{render(o, c.key)}</Box>)}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
